import React, { useEffect, useRef, useState } from "react";
import { getYears } from "../../../utils/date";
import { colorOptions, wineOrigins, SUBSCRIBERS_ONLY_HINT } from "../../../utils/wine";
import { useValidSubscription } from "../../../hooks/useValidSubscription";
import Checkbox from "../Inputs/Checkbox";
import DropDown from "../Inputs/DropDown"
import FilterWrapper from "../Wrappers/FilterWrapper";
import OverflowWrapper from "../Wrappers/OverflowWrapper";

export interface IFilter {
  vintageYearMin?: number,
  vintageYearMax?: number,
  scoreBetweenMin?: number,
  scoreBetweenMax?: number,
  priceBetweenMin?: number,
  priceBetweenMax?: number,
  reviewDateMin?: number,
  reviewDateMax?: number,
  createdAtFrom?: string,
  createdAtTo?: string,
  country?: string,
  color?: string,
  producerId?: string,
  articleId?: number,
  author?: string,
  searchable?: string,
}

const WineFilters = (props: { 
  className?: string, 
  onApply?: Function,
  values?: IFilter
}) => {
  const syncingFromPropsRef = useRef(false);
  const [filters, setFilters] = useState({} as IFilter);
  const hasValidSubscription = useValidSubscription();

  const areFiltersEqual = (left: IFilter = {}, right: IFilter = {}) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;

    return leftKeys.every((key) => left[key] === right[key]);
  }

  const handleCountryChange = (e) => {
    if(e.status) {
      setFilters({
        ...filters,
        country: e.value
      })
    } else if(filters.country) {
      const { country, ...nextFilters } = filters;
      setFilters(nextFilters)
    }
  }

  // const handleRegionChange = (e) => {
  //   if(e.status) {
  //     setFilters({
  //       ...filters,
  //       region: e.value
  //     })
  //   } else if(filters.region) {
  //     delete filters.region
  //     setFilters({
  //       ...filters
  //     })
  //   }
  // }

  const handleColorChange = (e) => {
    if(e.status) {
      setFilters({
        ...filters,
        color: e.value
      })
    } else if(filters.color) {
      const { color, ...nextFilters } = filters;
      setFilters(nextFilters)
    }
  }

  useEffect(() => {
    if (syncingFromPropsRef.current) {
      syncingFromPropsRef.current = false;
      return;
    }

    if (props.onApply) {
      props.onApply(filters);
    }
  }, [filters])

  useEffect(() => {
    const incomingFilters = props.values || {};
    if (!areFiltersEqual(incomingFilters, filters)) {
      syncingFromPropsRef.current = true;
      setFilters(incomingFilters);
    }
  }, [props.values]);

  return (
    <div className={`bg-white rounded w-full fixed top-0 left-0 right-0 z-50 p-4 pb-20 overflow-scroll h-full lg:relative lg:overflow-visible lg:pb-0 lg:p-0 lg:z-10 lg:w-1/3 xl:w-1/4 align-start ${ props.className }`}>
      <div className="sticky -top-4 lg:top-20 py-2 bg-white z-10 flex items-center justify-center">
        <h2 className="text-xl w-full">Filters</h2>
        <div className="flex">
          {
            Object.keys(filters).length > 0 &&
            <div 
              className="text-red cursor-pointer"
              onClick={() => {
                setFilters({});
              }}
              onKeyDown={(e) => {
                if(e.key === "Enter") {
                  setFilters({});
                }
              }}
              role="button"
              tabIndex={0}
            > 
              Reset
            </div>
          }

          <div 
            className="pl-4 text-red cursor-pointer lg:hidden"
            onClick={() => {
              props.onApply(filters)
            }}
            onKeyDown={(e) => {
              if(e.key === "Enter") {
                props.onApply(filters)
              }
            }}
            role="button"
            tabIndex={0}
          > 
            Apply
          </div>
        </div>
      </div>

      <FilterWrapper
        label="Vintage Between"
        flex
      >
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Min"
          value={filters.vintageYearMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              vintageYearMin: e
            })
          }}
        />
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Max"
          value={filters.vintageYearMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              vintageYearMax: e
            })
          }}
        />
      </FilterWrapper>

      <FilterWrapper
        label="Score Between"
        hint={hasValidSubscription ? undefined : SUBSCRIBERS_ONLY_HINT}
        flex
      >
        <DropDown
          type="line"
          options={Array.from({length: 100}, (value, key) => (key + 1)).reverse()}
          placeholder="Min"
          disabled={!hasValidSubscription}
          value={filters.scoreBetweenMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              scoreBetweenMin: e
            })
          }}
        />
        <DropDown
          type="line"
          options={Array.from({length: 100}, (value, key) => (key + 1)).reverse()}
          placeholder="Max"
          disabled={!hasValidSubscription}
          value={filters.scoreBetweenMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              scoreBetweenMax: e
            })
          }}
        />
      </FilterWrapper>

      <FilterWrapper
        label="Price Between"
        flex
      >
        <DropDown
          type="line"
          options={Array.from({length: 100}, (value, key) => (key + 1) * 10)}
          placeholder="Min"
          value={filters.priceBetweenMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              priceBetweenMin: e
            })
          }}
        />
        <DropDown
          type="line"
          options={Array.from({length: 100}, (value, key) => (key + 1) * 10)}
          placeholder="Max"
          value={filters.priceBetweenMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              priceBetweenMax: e
            })
          }}
        />
      </FilterWrapper>

      <FilterWrapper
        label="Review Date Between"
        flex
      >
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Min"
          value={filters.reviewDateMin}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              reviewDateMin: e
            })
          }}
        />
        <DropDown
          type="line"
          options={getYears(100)}
          placeholder="Max"
          value={filters.reviewDateMax}
          onOptionSelect={(e) => {
            setFilters({
              ...filters,
              reviewDateMax: e
            })
          }}
        />
      </FilterWrapper>

      <OverflowWrapper
        disableExpand={filters.country != undefined}
      >
        <FilterWrapper
          label="Country"
        >
          <div >
            {
              wineOrigins.filter((origin) => {
                return !filters.country || filters.country == origin
              }).map((origin) => {
                return (
                  <Checkbox
                    key={origin}
                    label={filters.country === origin ? origin + " (Remove)" : origin}
                    value={origin}
                    isActive={filters.country === origin}
                    onChange={(e) => {
                      handleCountryChange(e)
                    }}
                  />
                )
              })
            }
          </div>
        </FilterWrapper>
      </OverflowWrapper>

      {/* <OverflowWrapper
        disableExpand={filters.region != undefined}
      >
        <FilterWrapper
          label="Region"
        >
          <div >
            {
              wineOrigins.filter((origin) => {
                return !filters.region || filters.region == origin
              }).map((origin) => {
                return (
                  <Checkbox
                    key={origin}
                    label={filters.region === origin ? origin + " (Remove)" : origin}
                    value={origin}
                    isActive={filters.region === origin}
                    onChange={(e) => {
                      handleRegionChange(e);
                    }}
                  />
                )
              })
            }
          </div>
        </FilterWrapper>
      </OverflowWrapper> */}

      <OverflowWrapper
        disableExpand={filters.color != undefined}
      >
        <FilterWrapper
          label="Color"
        >
          <div >
            {
              colorOptions.filter((color) => {
                return !filters.color || filters.color === color
              }).map((color) => {
                return (
                  <Checkbox
                    key={color}
                    label={filters.color === color ? color + " (Remove)" : color}
                    value={color}
                    isActive={filters.color === color}
                    onChange={(e) => {
                      handleColorChange(e);
                    }}
                  />
                )
              })
            }
          </div>
        </FilterWrapper>
      </OverflowWrapper>


      
    </div>
  )
}

export default WineFilters;
